import React, { useState, useEffect, useMemo } from 'react';
import { X, Building2, Plus, Check, AlertCircle, FileText } from 'lucide-react';
import { EditableCell } from './EditableCell';
import { getSectionColor, getDataTypeInfo } from '../utils/colors';
import { ApiData } from '../types/api';

interface FormField {
  name: string;
  columnId: number;
  type: 'data' | 'número' | 'texto' | 'categoria_mercado' | 'yesno' | 'status_prospeccao' | 'status_industria' | 'percent' | 'distributors' | 'distributors_count';
  subsection: string;
  section: string;
  sectionId: number;
}

interface AddIndustryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (industryData: { name: string; numeroGoLive: string; fields: Record<string, string> }) => Promise<boolean>;
  data?: ApiData;
  editEnabled?: boolean;
}

export const AddIndustryModal: React.FC<AddIndustryModalProps> = ({
  isOpen,
  onClose,
  onSave,
  data,
  editEnabled = true,
}) => {
  const [industryName, setIndustryName] = useState('');
  const [numeroGoLive, setNumeroGoLive] = useState('');
  const [formFields, setFormFields] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Extract all unique fields from existing data
  const availableFields:any = useMemo((): FormField[] => {
    if (!data) return [];

    const fieldsMap = new Map<string, FormField>();

    Object.values(data).forEach(industry => {
      Object.entries(industry.sections).forEach(([sectionName, section]) => {
        Object.entries(section.columns).forEach(([columnName, column]) => {
          const key = `${sectionName}-${columnName}`;
          if (!fieldsMap.has(key)) {
            fieldsMap.set(key, {
              name: columnName,
              columnId: column.columnId,
              type: column.type,
              subsection: column.subsection,
              section: sectionName,
              sectionId: section.sectionId,
            });
          }
        });
      });
    });

    return Array.from(fieldsMap.values()).sort((a, b) => {
      // Sort by section first, then by name
      if (a.section !== b.section) {
        return a.section.localeCompare(b.section);
      }
      return a.name.localeCompare(b.name);
    });
  }, [data]);

  // Group fields by section
  const groupedFields = useMemo(() => {
    const groups: Record<string, FormField[]> = {};
    availableFields.forEach((field:any) => {
      if (!groups[field.section]) {
        groups[field.section] = [];
      }
      groups[field.section].push(field);
    });
    return groups;
  }, [availableFields]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setIndustryName('');
      setNumeroGoLive('');
      setErrors({});
      
      // Initialize form fields with empty values, except for 'fee' which should be '4'
     const initialFields: Record<string, string> = {};
      availableFields.forEach((field:any) => {
        if (field.name.toLowerCase() == 'fee') {
         initialFields[field.subsection] = '4';
        } else {
         initialFields[field.subsection] = '';
        }
      });
      setFormFields(initialFields);
    }
  }, [isOpen, availableFields]);

  const handleFieldChange = (subsection: string, value: string) => {
    const key = subsection;
    setFormFields(prev => ({
      ...prev,
      [key]: value,
    }));

    // Clear error for this field if it exists
    if (errors[key]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate industry name
    if (!industryName.trim()) {
      newErrors.industryName = 'Nome da indústria é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const success = await onSave({
        name: industryName.trim(),
        numeroGoLive: numeroGoLive.trim(),
        fields: formFields,
      });

      if (success) {
        onClose();
      }
    } catch (error) {
      console.error('Erro ao salvar indústria:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  if (!isOpen) return null;
  
  // Se edição estiver desabilitada, não permitir abertura do modal
  if (!editEnabled) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"  style={{zIndex: 10000}}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="relative overflow-hidden">
          <div className="flex items-center justify-between p-6 bg-blue-600 text-white">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm">
                <Plus className="h-7 w-7" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Adicionar Nova Indústria</h2>
                <p className="text-blue-100 mt-1">Preencha os dados da nova indústria</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Basic Info */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome da Indústria *
              </label>
              <input
                type="text"
                value={industryName}
                onChange={(e) => {
                  setIndustryName(e.target.value);
                  if (errors.industryName) {
                    setErrors(prev => ({ ...prev, industryName: '' }));
                  }
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.industryName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Digite o nome da indústria"
                disabled={isSaving}
              />
              {errors.industryName && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.industryName}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
               Número Go Live
              </label>
              <input
                type="text"
                value={numeroGoLive}
                onChange={(e) => {
                  setNumeroGoLive(e.target.value);
                  if (errors.numeroGoLive) {
                    setErrors(prev => ({ ...prev, numeroGoLive: '' }));
                  }
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.numeroGoLive ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Digite o número Go Live"
                disabled={isSaving}
              />
              {errors.numeroGoLive && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.numeroGoLive}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div className="flex-1 overflow-y-auto p-6">
          {Object.keys(groupedFields).length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum Campo Disponível</h3>
              <p className="text-gray-500">
                Não há dados existentes para usar como modelo. Adicione algumas indústrias primeiro.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(groupedFields).map(([sectionName, fields]) => {
                const sectionId = fields[0]?.sectionId || 0;
                const sectionColors = getSectionColor(sectionId);

                return (
                  <div key={sectionName} className={`bg-white rounded-xl shadow-sm border-2 ${sectionColors.border}`}>
                    <div className={`px-6 py-4 border-b ${sectionColors.border} ${sectionColors.bg}`}>
                      <div className="flex items-center space-x-3">
                        <FileText className={`h-5 w-5 ${sectionColors.icon}`} />
                        <h3 className={`text-lg font-semibold ${sectionColors.text}`}>{sectionName}</h3>
                        <span className="text-sm text-gray-500">({fields.length} campos)</span>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {fields.map((field) => {
                         const fieldKey = field.subsection;
                          const fieldValue = formFields[fieldKey] || '';

                          return (
                            <div key={fieldKey} className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">
                                <div className="flex items-center space-x-2">
                                  <span>{field.name}</span>
                                  <span
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getDataTypeInfo(field.type).bgColor} ${getDataTypeInfo(field.type).color}`}
                                    title={`Tipo: ${getDataTypeInfo(field.type).label}`}
                                  >
                                    {getDataTypeInfo(field.type).icon}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500">
                                  ({field.subsection})
                                </span>
                              </label>
                              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                <EditableCell
                                  value={fieldValue}
                                  type={field.type}
                                  onSave={async (newValue) => {
                                   handleFieldChange(field.subsection, newValue);
                                    return true;
                                  }}
                                  disabled={isSaving || !editEnabled}
                                />
                              </div>
                              {errors[fieldKey] && (
                                <p className="text-sm text-red-600 flex items-center">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  {errors[fieldKey]}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            {Object.keys(groupedFields).length > 0 && (
              <span>
                {availableFields.length} campos disponíveis em {Object.keys(groupedFields).length} seções
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || Object.keys(groupedFields).length === 0}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              <Check className="h-4 w-4" />
              <span>{isSaving ? 'Salvando...' : 'Salvar Indústria'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};